import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoMark } from '@/components/Logo';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Check your email for the reset link');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8" data-testid="forgot-home-link">
          <LogoMark size={40} />
          <span className="text-xl font-bold font-display tracking-tight">
            <span className="text-ink">Portfolio</span>
            <span className="text-signal-blue"> Pal</span>
          </span>
        </Link>

        <div className="bg-white rounded-2xl border border-line p-8 shadow-sm">
          {!submitted ? (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-ink font-display tracking-tight">Forgot password?</h1>
                <p className="mt-2 text-muted-brand">
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-slate-brand">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="forgot-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 h-11 rounded-xl border-line focus:border-signal-blue focus:ring-signal-blue"
                    disabled={loading}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="forgot-submit-btn"
                  className="w-full h-11 bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl font-semibold"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Sending...
                    </span>
                  ) : (
                    'Send reset link'
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-meadow-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-meadow-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-ink font-display mb-2">Check your email</h2>
              <p className="text-muted-brand mb-6">
                We&apos;ve sent a password reset link to <strong className="text-ink">{email}</strong>
              </p>
              <p className="text-sm text-muted-brand mb-6">
                Didn&apos;t receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => setSubmitted(false)}
                  data-testid="forgot-try-again-btn"
                  className="text-signal-blue hover:underline"
                >
                  try again
                </button>
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-line">
            <Link 
              to="/login"
              data-testid="forgot-back-to-login-link"
              className="flex items-center justify-center gap-2 text-sm text-muted-brand hover:text-ink transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
